import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { FLOW_SOLAR, FLOW_GRID, FLOW_BATTERY, FLOW_DEFAULT } from './twinColors';

/**
 * Color for energy flow particles — deep blue palette with source variation.
 */
function flowColor(from) {
    if (from === 'solar') return FLOW_SOLAR;
    if (from === 'grid') return FLOW_GRID;
    if (from === 'battery') return FLOW_BATTERY;
    return FLOW_DEFAULT;
}

/**
 * Clamp a value between min and max.
 */
function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

/**
 * SingleConduit - Particle stream along a curved cable-tray path.
 * Replaces tube geometry with animated point cloud for holographic aesthetic.
 */
const MAX_PARTICLES = 30;

function SingleConduit({ from, to, kw, fromPos, toPos }) {
    const pointsRef = useRef();
    const offsetsRef = useRef([]);

    const kwFraction = clamp(kw / 500, 0, 1);
    const color = flowColor(from);

    // Build cable-tray curve
    const curve = useMemo(() => {
        const src = new THREE.Vector3(fromPos[0], fromPos[1], fromPos[2]);
        const srcGround = new THREE.Vector3(fromPos[0], 0.5, fromPos[2]);
        const dstGround = new THREE.Vector3(toPos[0], 0.5, toPos[2]);
        const dst = new THREE.Vector3(toPos[0], toPos[1], toPos[2]);

        return new THREE.CatmullRomCurve3(
            [src, srcGround, dstGround, dst],
            false,
            'catmullrom',
            0.3
        );
    }, [fromPos, toPos]);

    // Speed proportional to kW
    const speed = 0.003 + kwFraction * 0.012;

    // Fixed-size buffer — never changes size after GPU upload
    const positions = useMemo(() => new Float32Array(MAX_PARTICLES * 3), []);

    useFrame(() => {
        if (!pointsRef.current) return;

        // Visible particle count proportional to kW (but buffer stays fixed)
        const activeCount = Math.max(3, Math.min(Math.ceil(Math.min(kw, 500) / 20), MAX_PARTICLES));

        // Grow offsets array if needed
        const offsets = offsetsRef.current;
        while (offsets.length < activeCount) {
            offsets.push(offsets.length / activeCount);
        }

        const arr = pointsRef.current.geometry.attributes.position.array;

        for (let i = 0; i < activeCount; i++) {
            offsets[i] = (offsets[i] + speed) % 1;
            const pt = curve.getPoint(offsets[i]);
            arr[i * 3] = pt.x;
            arr[i * 3 + 1] = pt.y;
            arr[i * 3 + 2] = pt.z;
        }

        // Only draw the active particles via drawRange
        pointsRef.current.geometry.setDrawRange(0, activeCount);
        pointsRef.current.geometry.attributes.position.needsUpdate = true;
    });

    return (
        <points ref={pointsRef}>
            <bufferGeometry>
                <bufferAttribute
                    attach="attributes-position"
                    count={MAX_PARTICLES}
                    array={positions}
                    itemSize={3}
                />
            </bufferGeometry>
            <pointsMaterial
                color={color}
                size={0.3}
                transparent
                opacity={0.7}
                depthWrite={false}
                sizeAttenuation
                blending={THREE.AdditiveBlending}
            />
        </points>
    );
}

/**
 * EnergyFlowConduits - Renders particle-based energy flows between equipment.
 * Point cloud aesthetic replacing tube geometry for holographic look.
 *
 * @param {{
 *   flows: Array<{from: string, to: string, kw: number}>,
 *   equipmentPositions: Object<string, number[]>
 * }} props
 */
export default function EnergyFlowConduits({ flows, equipmentPositions }) {
    if (!flows || !equipmentPositions) return null;

    const activeFlows = flows.filter(f => f.kw > 0);

    return (
        <group>
            {activeFlows.map((flow, i) => {
                const fromPos = equipmentPositions[flow.from];
                const toPos = equipmentPositions[flow.to];
                if (!fromPos || !toPos) return null;

                return (
                    <SingleConduit
                        key={`${flow.from}-${flow.to}-${i}`}
                        from={flow.from}
                        to={flow.to}
                        kw={flow.kw}
                        fromPos={fromPos}
                        toPos={toPos}
                    />
                );
            })}
        </group>
    );
}
